import React, { useState, useEffect } from 'react';
import axios from './utils/axiosConfig';
import { Container, Row, Col, Card, Spinner, Alert, Form, Button } from 'react-bootstrap';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useAuth } from './context/AuthContext';
import { MEMBER_TERM } from './config/clubTerms';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

const MemberAnalytics = () => {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [genderAgeData, setGenderAgeData] = useState(null);
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState('');
  const [loading, setLoading] = useState(true);
  const [storesLoading, setStoresLoading] = useState(true);
  const [error, setError] = useState(null);
  const token = localStorage.getItem('token');

  const fetchStores = async () => {
    setStoresLoading(true);

    if (!token) {
      setError('請先登入系統才能查看分析資料');
      setStores([]);
      setStoresLoading(false);
      return;
    }

    try {
      const response = await axios.get('/member-analytics/stores', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.data && response.data.success && response.data.data) {
        setStores(response.data.data);
      } else {
        setStores([]);
      }
    } catch (error) {
      console.error('載入社別資料失敗');
      if (error.response?.status === 401) {
        setError('登入已過期，請重新登入');
      } else {
        setError('無法載入社別資料，請稍後再試');
      }
      setStores([]);
    }
    setStoresLoading(false);
  };

  const fetchAnalyticsData = async (storeId = '') => {
    setLoading(true);

    if (!token) {
      setError('請先登入系統才能查看分析資料');
      setLoading(false);
      return;
    }
    
    try {
      const params = storeId ? { ss_id: storeId } : {};
      
      const [analyticsResponse, genderAgeResponse] = await Promise.all([
        axios.get('/member-analytics', {
          headers: { 'Authorization': `Bearer ${token}` },
          params
        }),
        axios.get('/member-analytics/gender-age', {
          headers: { 'Authorization': `Bearer ${token}` },
          params
        })
      ]);
      
      setAnalyticsData(analyticsResponse.data);
      setGenderAgeData(genderAgeResponse.data);
      setError(null);
    } catch (error) {
      console.error('Error fetching analytics data:', error);
      if (error.response?.status === 401) {
        setError('登入已過期，請重新登入');
      } else {
        setError('無法載入分析資料，請稍後再試。');
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchStores();
    fetchAnalyticsData();
  }, []);

  const handleStoreChange = (e) => {
    const storeId = e.target.value;
    setSelectedStore(storeId);
    fetchAnalyticsData(storeId);
  };

  const getCurrentStoreName = () => {
    if (!selectedStore) return '全國';
    if (!stores || stores.length === 0) return '全國';
    const store = stores.find(s => s.ss_id.toString() === selectedStore);
    return store ? store.ss_name : '全國';
  };

  const generatePieChartData = (data, label, labelField = 'store_name') => {
    if (!data || data.length === 0) return null;
    
    // 過濾掉沒有數量的項目
    const validData = data.filter(item => (item.count || item.total || 0) > 0);
    if (validData.length === 0) return null;
    
    const colors = [
      '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
      '#FF9F40', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4',
      '#FFEAA7', '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE'
    ];

    // 計算總數用於百分比計算
    const total = validData.reduce((sum, item) => sum + (item.count || item.total || 0), 0);
    if (total === 0) return null;

    return {
      labels: validData.map(item => {
        const name = item[labelField] || 
          item.store_name || 
          item.gender || 
          item.age_group || 
          `項目 ${item.store_id || ''}`;
        const count = item.count || item.total || 0;
        const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : '0.0';
        return `${name} (${count}人, ${percentage}%)`;
      }),
      datasets: [{
        label: label,
        data: validData.map(item => item.count || item.total || 0),
        backgroundColor: colors.slice(0, validData.length),
        borderColor: colors.slice(0, validData.length).map(color => color.replace('0.6', '1')),
        borderWidth: 1
      }]
    };
  };

  // 專門用於性別分佈的圓餅圖，對調男女顏色
  const generateGenderPieChartData = (data, label) => {
    if (!data || data.length === 0) return null;
    
    // 過濾掉沒有數量的項目
    const validData = data.filter(item => (item.count || item.total || 0) > 0);
    if (validData.length === 0) return null;
    
    // 計算總數用於百分比計算
    const total = validData.reduce((sum, item) => sum + (item.count || item.total || 0), 0);
    if (total === 0) return null;

    // 為每個性別指定特定顏色
    const getGenderColor = (gender) => {
      if (gender === '男性') return '#36A2EB'; // 藍色給男性
      if (gender === '女性') return '#FF6384'; // 粉紅色給女性
      return '#FFCE56'; // 黃色給未設定
    };

    return {
      labels: validData.map(item => {
        const name = item.gender || '未設定';
        const count = item.count || item.total || 0;
        const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : '0.0';
        return `${name} (${count}人, ${percentage}%)`;
      }),
      datasets: [{
        label: label,
        data: validData.map(item => item.count || item.total || 0),
        backgroundColor: validData.map(item => getGenderColor(item.gender)),
        borderColor: validData.map(item => getGenderColor(item.gender)),
        borderWidth: 1
      }]
    };
  };

  const generateBarChartData = (data, label) => {
    if (!data || data.length === 0) return null;

    // 過濾掉沒有數量的項目
    const validData = data.filter(item => (item.count || item.total || 0) > 0);
    if (validData.length === 0) return null;

    return {
      labels: validData.map(item => {
        const name = item.store_name || item.age_group || `項目 ${item.store_id || ''}`;
        const count = item.count || item.total || 0;
        return `${name}\n(${count}人)`;
      }),
      datasets: [{
        label: label,
        data: validData.map(item => item.count || item.total || 0),
        backgroundColor: 'rgba(54, 162, 235, 0.6)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1
      }]
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 12,
          padding: 8,
          font: {
            size: 11
          }
        }
      },
      title: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            return label;
          }
        }
      },
    },
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        ticks: {
          maxRotation: 45,
          minRotation: 0,
          font: {
            size: 11
          }
        }
      },
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1
        }
      }
    },
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 12,
          padding: 8,
          font: {
            size: 11
          }
        }
      },
      title: {
        display: false,
      },
      tooltip: {
        callbacks: {
          title: function(context) {
            // 顯示 X 軸標籤作為 tooltip 標題
            return context[0].label;
          },
          label: function(context) {
            const label = context.dataset.label || '';
            const value = context.parsed.y;
            return `${label}: ${value}人`;
          }
        }
      }
    },
    // 使用 onHover 來改變滑鼠樣式
    onHover: (event, activeElements, chart) => {
      if (activeElements && activeElements.length > 0) {
        chart.canvas.style.cursor = 'pointer';
      } else {
        chart.canvas.style.cursor = 'default';
      }
    }
  };

  if (loading) {
    return (
      <Container className="mt-4 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">載入中...</span>
        </Spinner>
        <p className="mt-2">載入分析資料中...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          <Alert.Heading>無法載入資料</Alert.Heading>
          <p>{error}</p>
          {error.includes('登入') && (
            <>
              <hr />
              <div className="d-flex justify-content-end">
                <Button variant="outline-danger" href="/login">
                  前往登入頁面
                </Button>
              </div>
            </>
          )}
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4">
      <Row className="mb-4">
        <Col>
          <h2 className="text-center">{getCurrentStoreName()}{MEMBER_TERM}分析圖表</h2>
        </Col>
      </Row>
      
      {/* 社別選擇器 */}
      <Row className="mb-4">
        <Col md={4} className="mx-auto">
          <Card>
            <Card.Body>
              <Form.Group>
                <Form.Label><strong>選擇查看範圍：</strong></Form.Label>
                <Form.Select 
                  value={selectedStore} 
                  onChange={handleStoreChange}
                  className="form-select-lg"
                  disabled={storesLoading}
                >
                  <option value="">全國{MEMBER_TERM}</option>
                  {!storesLoading && stores && stores.length > 0 && stores.map(store => (
                    <option key={store.ss_id} value={store.ss_id}>
                      {store.ss_name}
                    </option>
                  ))}
                  {storesLoading && (
                    <option disabled>載入社別資料中...</option>
                  )}
                </Form.Select>
              </Form.Group>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col lg={6} className="mb-4">
          <Card>
            <Card.Header>
              <h5>各社{MEMBER_TERM}人數</h5>
            </Card.Header>
            <Card.Body style={{ height: '400px' }}>
              {analyticsData?.membersByStore && analyticsData.membersByStore.length > 0 ? (
                (() => {
                  const chartData = generatePieChartData(analyticsData.membersByStore, `${MEMBER_TERM}人數`);
                  return chartData ? (
                    <Pie data={chartData} options={chartOptions} />
                  ) : (
                    <div className="d-flex align-items-center justify-content-center h-100">
                      <p className="text-muted">暫無資料可顯示</p>
                    </div>
                  );
                })()
              ) : (
                <div className="d-flex align-items-center justify-content-center h-100">
                  <p className="text-muted">暫無資料可顯示</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6} className="mb-4">
          <Card>
            <Card.Header>
              <h5>各社{MEMBER_TERM}人數</h5>
            </Card.Header>
            <Card.Body style={{ height: '400px' }}>
              {analyticsData?.membersByStore && analyticsData.membersByStore.length > 0 ? (
                (() => {
                  const chartData = generateBarChartData(analyticsData.membersByStore, `${MEMBER_TERM}人數`);
                  return chartData ? (
                    <Bar data={chartData} options={barChartOptions} />
                  ) : (
                    <div className="d-flex align-items-center justify-content-center h-100">
                      <p className="text-muted">暫無資料可顯示</p>
                    </div>
                  );
                })()
              ) : (
                <div className="d-flex align-items-center justify-content-center h-100">
                  <p className="text-muted">暫無資料可顯示</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col lg={6} className="mb-4">
          <Card>
            <Card.Header>
              <h5>各社數位化</h5>
            </Card.Header>
            <Card.Body style={{ height: '400px' }}>
              {analyticsData?.activeMembersByStore && analyticsData.activeMembersByStore.length > 0 ? (
                (() => {
                  const chartData = generatePieChartData(analyticsData.activeMembersByStore, `活躍${MEMBER_TERM}`);
                  return chartData ? (
                    <Pie data={chartData} options={chartOptions} />
                  ) : (
                    <div className="d-flex align-items-center justify-content-center h-100">
                      <p className="text-muted">暫無資料可顯示</p>
                    </div>
                  );
                })()
              ) : (
                <div className="d-flex align-items-center justify-content-center h-100">
                  <p className="text-muted">暫無資料可顯示</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6} className="mb-4">
          <Card>
            <Card.Header>
              <h5>各社職位</h5>
            </Card.Header>
            <Card.Body style={{ height: '400px' }}>
              {analyticsData?.positionsByStore && analyticsData.positionsByStore.length > 0 ? (
                (() => {
                  const chartData = generateBarChartData(analyticsData.positionsByStore, '職位數量');
                  return chartData ? (
                    <Bar data={chartData} options={barChartOptions} />
                  ) : (
                    <div className="d-flex align-items-center justify-content-center h-100">
                      <p className="text-muted">暫無資料可顯示</p>
                    </div>
                  );
                })()
              ) : (
                <div className="d-flex align-items-center justify-content-center h-100">
                  <p className="text-muted">暫無資料可顯示</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Header>
              <h5>統計摘要</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={3} className="text-center mb-3">
                  <h4 className="text-primary">{analyticsData?.summary?.totalMembers || 0}</h4>
                  <p>總{MEMBER_TERM}人數</p>
                </Col>
                <Col md={3} className="text-center mb-3">
                  <h4 className="text-success">{analyticsData?.summary?.activeMembers || 0}</h4>
                  <p>活躍{MEMBER_TERM}</p>
                </Col>
                <Col md={3} className="text-center mb-3">
                  <h4 className="text-info">{analyticsData?.summary?.totalStores || 0}</h4>
                  <p>社別總數</p>
                </Col>
                <Col md={3} className="text-center mb-3">
                  <h4 className="text-warning">{analyticsData?.summary?.averageMembersPerStore || 0}</h4>
                  <p>平均每社人數</p>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* 社友男女及年齡分析 */}
      <Row className="mb-4">
        <Col>
          <h3 className="text-center mb-4">{getCurrentStoreName()}{MEMBER_TERM}男女及年齡分析</h3>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col lg={6} className="mb-4">
          <Card>
            <Card.Header>
              <h5>{MEMBER_TERM}性別</h5>
            </Card.Header>
            <Card.Body style={{ height: '400px' }}>
              {genderAgeData?.genderAnalysis && genderAgeData.genderAnalysis.length > 0 ? (
                (() => {
                  const chartData = generateGenderPieChartData(genderAgeData.genderAnalysis, '人數');
                  return chartData ? (
                    <Pie data={chartData} options={chartOptions} />
                  ) : (
                    <div className="d-flex align-items-center justify-content-center h-100">
                      <p className="text-muted">暫無資料可顯示</p>
                    </div>
                  );
                })()
              ) : (
                <div className="d-flex align-items-center justify-content-center h-100">
                  <p className="text-muted">暫無資料可顯示</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6} className="mb-4">
          <Card>
            <Card.Header>
              <h5>{MEMBER_TERM}年齡 </h5>
            </Card.Header>
            <Card.Body style={{ height: '400px' }}>
              {genderAgeData?.ageAnalysis && genderAgeData.ageAnalysis.length > 0 ? (
                (() => {
                  const chartData = generateBarChartData(genderAgeData.ageAnalysis.map(item => ({
                    ...item,
                    store_name: item.age_group
                  })), '人數');
                  return chartData ? (
                    <Bar data={chartData} options={barChartOptions} />
                  ) : (
                    <div className="d-flex align-items-center justify-content-center h-100">
                      <p className="text-muted">暫無資料可顯示</p>
                    </div>
                  );
                })()
              ) : (
                <div className="d-flex align-items-center justify-content-center h-100">
                  <p className="text-muted">暫無資料可顯示</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Header>
              <h5>男女年齡交叉分析</h5>
            </Card.Header>
            <Card.Body>
              <div style={{ height: '500px' }}>
                {genderAgeData?.genderAgeAnalysis && genderAgeData.genderAgeAnalysis.length > 0 ? (() => {
                  // 定義完整的年齡組別順序（由年輕到年長）
                  const orderedAgeGroups = [
                    '20歲以下', '20-24歲', '25-29歲', '30-34歲', '35-39歲',
                    '40-44歲', '45-49歲', '50-54歲', '55-59歲', '60-64歲',
                    '65-69歲', '70-74歲', '75歲以上', '年齡未設定'
                  ];

                  // 組織數據為男女分組
                  const maleData = genderAgeData.genderAgeAnalysis
                    .filter(item => item.gender === '男性')
                    .reduce((acc, item) => {
                      acc[item.age_group] = item.count;
                      return acc;
                    }, {});
                  
                  const femaleData = genderAgeData.genderAgeAnalysis
                    .filter(item => item.gender === '女性')
                    .reduce((acc, item) => {
                      acc[item.age_group] = item.count;
                      return acc;
                    }, {});

                  // 只顯示有資料的年齡組別，但保持順序
                  const availableAgeGroups = orderedAgeGroups.filter(ageGroup => 
                    maleData[ageGroup] > 0 || femaleData[ageGroup] > 0
                  );
                  
                  const chartData = {
                    labels: availableAgeGroups,
                    datasets: [
                      {
                        label: '男性',
                        data: availableAgeGroups.map(ageGroup => maleData[ageGroup] || 0),
                        backgroundColor: 'rgba(54, 162, 235, 0.6)',
                        borderColor: 'rgba(54, 162, 235, 1)',
                        borderWidth: 1
                      },
                      {
                        label: '女性',
                        data: availableAgeGroups.map(ageGroup => femaleData[ageGroup] || 0),
                        backgroundColor: 'rgba(255, 99, 132, 0.6)',
                        borderColor: 'rgba(255, 99, 132, 1)',
                        borderWidth: 1
                      }
                    ]
                  };

                  return <Bar data={chartData} options={barChartOptions} />;
                })() : (
                  <div className="d-flex align-items-center justify-content-center h-100">
                    <p className="text-muted">暫無男女年齡交叉分析資料</p>
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default MemberAnalytics;